import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { PasswordSchema, type TwoFactorSetup } from '@iptv/shared-types';
import { Alert, useToast } from '../components/feedback.js';
import { Button, Card, Field, Input, Spinner } from '../components/primitives.js';
import { PageHeader } from '../components/data.js';
import { Avatar, BrandMark, LanguageSwitcher, ThemeToggle } from './shell.js';
import { useAuth, WrongPanelError } from './auth.js';
import { isApiError, useErrorText } from './errors.js';
import { AuthenticatorSetup, CodeField, RecoveryCodesPanel, TwoFactorCard } from './two-factor.js';

type Step =
  | { kind: 'password' }
  | { kind: 'code'; challengeToken: string }
  | { kind: 'setup'; challengeToken: string; setup: TwoFactorSetup | null }
  | { kind: 'codes'; codes: string[]; enter: () => void };

export function LoginPage() {
  const { t } = useTranslation();
  const { user, signIn, verifySignIn, api, audience, sessionLost, restoring } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const errorText = useErrorText();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'password' });
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const form = useForm<{ email: string; password: string }>({
    defaultValues: { email: '', password: '' },
  });

  if (!restoring && user && step.kind !== 'codes') return <Navigate to="/" replace />;

  const goIn = () => {
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== '/login' ? from : '/', { replace: true });
  };
  const restart = (message: string | null) => {
    setStep({ kind: 'password' });
    setCode('');
    setError(message);
    form.resetField('password');
  };

  const onPassword = form.handleSubmit(async ({ email, password }) => {
    setError(null);
    try {
      const result = await signIn(email.trim(), password);
      if (!('challengeToken' in result)) return goIn();
      setCode('');
      if (result.twoFactor === 'VERIFY') {
        setStep({ kind: 'code', challengeToken: result.challengeToken });
        return;
      }
      setStep({ kind: 'setup', challengeToken: result.challengeToken, setup: null });
      const setup = await api.loginSetup(result.challengeToken);
      setStep({ kind: 'setup', challengeToken: result.challengeToken, setup });
    } catch (err) {
      restart(err instanceof WrongPanelError ? t('auth.wrongPanel') : errorText(err));
    }
  });

  const onCode = async (challengeToken: string) => {
    if (code.trim().length < 6 || busy) return;
    setError(null);
    setBusy(true);
    try {
      const { recoveryCodes, enter } = await verifySignIn(challengeToken, code);
      if (recoveryCodes) {
        setStep({ kind: 'codes', codes: recoveryCodes, enter });
      } else {
        enter();
        goIn();
      }
    } catch (err) {
      // An expired or used-up challenge cannot be retried: start again from the password.
      if (isApiError(err, 'UNAUTHENTICATED') || isApiError(err, 'RATE_LIMITED'))
        restart(t('twoFactor.expired'));
      else if (err instanceof WrongPanelError) restart(t('auth.wrongPanel'));
      else if (isApiError(err, 'INVALID_CODE') || isApiError(err, 'VALIDATION_FAILED'))
        setError(t('errors.INVALID_CODE'));
      else if (isApiError(err)) restart(errorText(err));
      else setError(errorText(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const title =
    step.kind === 'password'
      ? t('auth.signInTitle')
      : step.kind === 'code'
        ? t('twoFactor.title')
        : step.kind === 'setup'
          ? t('twoFactor.setupTitle')
          : t('twoFactor.recoveryCodes');
  const subtitle =
    step.kind === 'password'
      ? t(audience === 'admin' ? 'auth.signInSubtitleAdmin' : 'auth.signInSubtitleMerchant')
      : step.kind === 'code'
        ? t('twoFactor.enterCode')
        : step.kind === 'setup'
          ? t('twoFactor.setupRequired')
          : t('twoFactor.recoveryIntro');

  return (
    <main
      className="relative flex min-h-dvh flex-col items-center justify-center px-4 py-10"
      style={{
        paddingTop: 'max(2.5rem, var(--safe-top))',
        paddingBottom: 'max(2.5rem, var(--safe-bottom))',
      }}
    >
      <div
        className="absolute end-4 top-4 flex items-center gap-0.5"
        style={{ marginTop: 'var(--safe-top)' }}
      >
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-[400px] animate-rise">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <BrandMark className="size-14 rounded-[18px] [&_svg]:size-7" />
          <span className="text-sm font-medium text-muted">
            {t(audience === 'admin' ? 'common.appTitleAdmin' : 'common.appTitleMerchant')}
          </span>
        </div>
        <div className="material relative rounded-panel p-6 shadow-xl sm:p-8">
          <h1 className="text-2xl font-semibold ltr:tracking-tight">{title}</h1>
          <p className="mt-1.5 text-sm text-muted">{subtitle}</p>
          {step.kind === 'password' && (
            <form onSubmit={onPassword} className="mt-7 space-y-5" noValidate>
              {sessionLost && !error && <Alert tone="info">{t('auth.sessionExpired')}</Alert>}
              {error && <Alert tone="danger">{error}</Alert>}
              <Field
                label={t('auth.email')}
                error={form.formState.errors.email && t('validation.required')}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="email"
                    autoComplete="username"
                    inputMode="email"
                    dir="ltr"
                    {...form.register('email', { required: true })}
                  />
                )}
              </Field>
              <Field
                label={t('auth.password')}
                error={form.formState.errors.password && t('validation.required')}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="password"
                    autoComplete="current-password"
                    dir="ltr"
                    {...form.register('password', { required: true })}
                  />
                )}
              </Field>
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>
          )}
          {(step.kind === 'code' || step.kind === 'setup') && (
            <form
              className="mt-7 space-y-5"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void onCode(step.challengeToken);
              }}
            >
              {error && <Alert tone="danger">{error}</Alert>}
              {step.kind === 'setup' &&
                (step.setup ? (
                  <AuthenticatorSetup setup={step.setup} />
                ) : (
                  <Spinner className="h-48" />
                ))}
              <CodeField value={code} onChange={setCode} autoFocus={step.kind === 'code'} />
              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={busy}
                disabled={code.trim().length < 6 || (step.kind === 'setup' && !step.setup)}
              >
                {t('twoFactor.verify')}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => restart(null)}>
                {t('twoFactor.back')}
              </Button>
            </form>
          )}
          {step.kind === 'codes' && (
            <div className="mt-7 space-y-5">
              <RecoveryCodesPanel codes={step.codes} />
              <Button
                size="lg"
                className="w-full"
                onClick={() => {
                  step.enter();
                  goIn();
                }}
              >
                {t('twoFactor.savedCodes')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function AccountPage() {
  const { t } = useTranslation();
  const { user, api } = useAuth();
  const toast = useToast();
  const errorText = useErrorText();
  const form = useForm<PasswordForm>({
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });
  const { errors } = form.formState;

  const change = useMutation({
    mutationFn: (v: PasswordForm) => api.changePassword(v.currentPassword, v.newPassword),
    onSuccess: () => {
      toast.success(t('auth.passwordChanged'));
      form.reset();
    },
    onError: (err) => toast.error(errorText(err)),
  });

  if (!user) return null;
  return (
    <>
      <PageHeader title={t('nav.account')} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t('auth.profile')}>
          <div className="mb-5 flex items-center gap-4">
            <Avatar name={user.displayName} className="size-14 text-lg" />
            <div className="min-w-0">
              <div className="truncate text-lg font-semibold">{user.displayName}</div>
              <div className="ltr-iso truncate text-sm text-muted">{user.email}</div>
            </div>
          </div>
          <dl className="divide-y divide-[var(--ui-line)] text-sm">
            <div className="flex items-center justify-between gap-4 py-3">
              <dt className="text-muted">{t('common.name')}</dt>
              <dd className="min-w-0 truncate font-medium">{user.displayName}</dd>
            </div>
            <div className="flex items-center justify-between gap-4 py-3">
              <dt className="text-muted">{t('common.email')}</dt>
              <dd className="ltr-iso min-w-0 truncate font-medium">{user.email}</dd>
            </div>
            <div className="flex items-center justify-between gap-4 py-3">
              <dt className="text-muted">{t('auth.role')}</dt>
              <dd className="font-medium">{t(`roles.${user.role}`)}</dd>
            </div>
          </dl>
        </Card>
        <Card title={t('auth.changePassword')}>
          <form
            onSubmit={form.handleSubmit((v) => change.mutate(v))}
            className="space-y-4"
            noValidate
          >
            <Field label={t('auth.currentPassword')} error={errors.currentPassword?.message}>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="current-password"
                  dir="ltr"
                  {...form.register('currentPassword', { required: t('validation.required') })}
                />
              )}
            </Field>
            <Field
              label={t('auth.newPassword')}
              hint={t('auth.passwordRules')}
              error={errors.newPassword?.message}
            >
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  dir="ltr"
                  {...form.register('newPassword', {
                    validate: (v) => PasswordSchema.safeParse(v).success || t('auth.passwordRules'),
                  })}
                />
              )}
            </Field>
            <Field label={t('auth.confirmPassword')} error={errors.confirmPassword?.message}>
              {(p) => (
                <Input
                  {...p}
                  type="password"
                  autoComplete="new-password"
                  dir="ltr"
                  {...form.register('confirmPassword', {
                    validate: (v, all) => v === all.newPassword || t('auth.passwordsDontMatch'),
                  })}
                />
              )}
            </Field>
            <div className="flex justify-end">
              <Button type="submit" loading={change.isPending} className="max-sm:w-full">
                {t('auth.changePassword')}
              </Button>
            </div>
          </form>
        </Card>
        <TwoFactorCard />
      </div>
    </>
  );
}
